// pages/SeasonsPage.jsx
import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import PageShell from "../components/PageShell";

/**
 * Seasons page (mobile-first)
 * - xs: seasons show as clean list-items (like your Standings mobile list)
 * - sm+: table stays, but the "Season" cell uses the same list-item vibe (image + bold title + subline)
 * - Cards grid remains for highlights
 *
 * Props: { data }
 */
export default function SeasonsPage({ data }) {
  const intro =
    data?.seasons?.intro ??
    data?.seasonsIntro ??
    "Browse past and current seasons, classes, and champions.";

  const list = data?.seasons?.seasonsList ?? [];
  const schedule = data?.schedule?.events ?? [];

  const seasons = useMemo(() => {
    return [...list].sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
  }, [list]);

  const roundsBySeasonKey = useMemo(() => {
    const m = new Map();
    for (const ev of schedule || []) {
      const key = `${ev.game ?? ""}__${ev.season ?? ""}`;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [schedule]);

  const getRounds = (s) => schedule.filter((s1) => {
    return s1?.season == s.season
  }).length ?? 0;

  const SeasonRowItem = ({ season, index, showDivider = true }) => {
    const classesText = Array.isArray(season.classes)
      ? season.classes.join(", ")
      : season.classes ?? "";

    return (
      <Box
        sx={{
          p: 1.5,
          "&:active": { opacity: 0.85 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 34,
              textAlign: "center",
              fontWeight: 900,
              fontSize: 14,
              opacity: 0.9,
              flex: "0 0 auto",
            }}
          >
            {index + 1}
          </Box>

          <Box
            sx={{
              width: 52,
              height: 40,
              borderRadius: 2,
              overflow: "hidden",
              flex: "0 0 auto",
              bgcolor: "action.hover",
            }}
          >
            {season.image ? (
              <Box
                component="img"
                src={season.image}
                alt={season.name}
                sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            ) : null}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 900 }} noWrap>
              {season.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {season.year ?? "—"} • Champion: {season.winner || "TBD"}
              {season.team ? ` • ${season.team}` : ""}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.75 }}>
              <Chip size="small" label={`${getRounds(season)} rounds`} />
              <Chip size="small" label={`Drop ${season.dropRounds ?? 0}`} />
              {classesText ? (
                <Chip size="small" label={classesText} sx={{ maxWidth: "100%" }} />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        {showDivider && <Divider sx={{ mt: 1.5 }} />}
      </Box>
    );
  };

  return (
    <PageShell title="Seasons" bannerImage="pit.png">
      <Typography sx={{ mb: 2 }}>{intro}</Typography>

      {/* Mobile-first: list items on xs */}
      {seasons.length > 0 ? (
        <>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
              {seasons.map((s, i) => (
                <SeasonRowItem
                  key={`${s.name}-${s.year ?? ""}-${i}`}
                  season={s}
                  index={i}
                  showDivider={i !== seasons.length - 1}
                />
              ))}
            </Paper>
          </Box>

          {/* Desktop/tablet: table with "list-item" season cell */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table size="small" sx={{ minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Season</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Winner</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Classes</TableCell>
                    <TableCell align="right">Rounds</TableCell>
                    <TableCell align="right">Drop Rounds</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {seasons.map((s) => {
                    const classesText = Array.isArray(s.classes)
                      ? s.classes.join(", ")
                      : s.classes ?? "";

                    return (
                      <TableRow key={`${s.name}-${s.year ?? ""}`}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                width: 56,
                                height: 42,
                                borderRadius: 2,
                                overflow: "hidden",
                                flex: "0 0 auto",
                                bgcolor: "action.hover",
                              }}
                            >
                              {s.image ? (
                                <Box
                                  component="img"
                                  src={s.image}
                                  alt={s.name}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              ) : null}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 900 }} noWrap>
                                {s.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                Champion: {s.winner || "TBD"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>{s.year}</TableCell>
                        <TableCell>{s.winner}</TableCell>
                        <TableCell>{s.team}</TableCell>
                        <TableCell>{classesText}</TableCell>
                        <TableCell align="right">{getRounds(s)}</TableCell>
                        <TableCell align="right">{s.dropRounds}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </>
      ) : null}

      {/* Season cards with images (highlights) */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {seasons.slice(0, 6).map((season) => (
          <Grid key={`${season.name}-card`} item xs={12} sm={6} md={4}>
            <Card sx={{ height: "100%", borderRadius: 3, overflow: "hidden" }}>
              <CardMedia component="img" height="140" image={season.image} alt={season.name} />
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {season.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Champion: {season.winner || "TBD"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </PageShell>
  );
}
